import { observable, runInAction } from 'mobx'
import { task, Task } from 'mobx-task'
import { axiosGet, axiosPost } from '../utils/axios'
import apiUrl from '../configs/apiUrl'
import { QuestionnaireStatus } from '../controls/Type'

export type LookupResponseColorChart = {
    id: number
    color: string
}

export type AutoCompleteType = {
    label: string
    value: number | string
}

export type LookupResponseCommunityCategory = {
    id: number
    name: string
}

export type LookupResponseReward = {
    id: number
    name: string
    institution: string
}

export type LookupResponseTitleName = {
    prefixID: number
    nameTH: string
    nameEN: string
}

export type LookupResponseProvince = {
    provID: number
    provNameTH: string
    provNameEN: string
    clusterID: string[]
}

export type LookupResponseDistrict = {
    provID: number
    ampID: number
    ampNameTH: string
    ampNameEN: string
}

export type LookupResponseSubdistrict = {
    provID: number
    ampID: number
    tamID: number
    tamNameTH: string
    tamNameEN: string
}

export type LookupResponseCommunity = {
    commID: string
    commNameTH: string
    commNameEN: string
    commAddrTH: string
    commAddrEN: string
    contactNO: string
    commZoneID: string
    provID: number
    tamID: number
    ampID: number
    postCode: string
}

export type LookupResponse = {
    label: string
    value: number | string
    childValue?: string[] | undefined
    clusterID?: string
}

export type LookupRewardResponse = {
    label: string
    value: number | string
    institution: string
}

export type ClusterResponse = {
    clusterID: string
    clusterTH: string
    clusterEN: string
    provID: string[]
}

export type LookupQuestionnaireStatusResponse = {
    questStatusCode: QuestionnaireStatus
    questStatusNameEN: string
    questStatusNameTH: string
    questStatusColor: string
}

export type LookupQuestionnaireGrade = {
    grade: string
    percent: number
    description: string
}

export type LookupResponseCommunitySearch = {
    communityID: string
    communityNameEN: string
    communityNameTH: string
    provCode: string
    provNameTH: string
    ampCode: number
    ampNameTH: string
    tamCode: 0
    tamNameTH: string
    regionCode: 0
    regionNameTH: string
    clusterID: string
    clusterNameTH: string
    contactTel: string
    communityImage: string
    isActiveAssessment: Boolean
    countAssessmentAdmin: number
    isDASTA: Boolean
}

export default class {
    // @action example = () => {}

    // คำนำหน้าชื่อ
    @observable titleName: LookupResponse[] = []
    @task.resolved getLookupTitleName = (async () => {
        if (this.titleName.length === 0) {
            const { request } = axiosGet<LookupResponseTitleName[]>({ url: apiUrl.getLookupTitleName })
            const { data } = await request
            if (data) {
                runInAction(() => {
                    const lookupTitleName = data.map((item) => ({
                        label: item.nameTH,
                        value: item.prefixID,
                    }))
                    this.titleName = lookupTitleName
                })
            }
            return data || []
        }
    }) as Task<[], Promise<LookupResponseTitleName[]>>

    // จังหวัด
    @observable provinces: LookupResponse[] = []
    @task.resolved getLookupProvince = (async () => {
        if (this.provinces.length === 0) {
            const { request } = axiosGet<LookupResponseProvince[]>({ url: apiUrl.getLookupProvince })
            const { data } = await request
            if (data) {
                runInAction(() => {
                    const lookup = data.map((item) => ({
                        label: item.provNameTH,
                        value: item.provID,
                    }))

                    this.provinces = lookup
                })
            }
            return data || []
        }
    }) as Task<[], Promise<LookupResponseProvince[]>>

    // จังหวัดที่มีชุมชนอยู่
    @observable provinceCommunity: LookupResponse[] = []
    @task.resolved getLookupProvinceCommunity = (async () => {
        if (this.provinceCommunity.length === 0) {
            const { request } = axiosGet<LookupResponseProvince[]>({
                url: apiUrl.getLookupProvinceCommunity,
            })
            const { data } = await request
            if (data) {
                runInAction(() => {
                    const lookup = data.map((item) => ({
                        label: item.provNameTH,
                        value: item.provID,
                        childValue: item.clusterID,
                    }))
                    this.provinceCommunity = lookup
                })
            }
            return data || []
        }
    }) as Task<[], Promise<LookupResponseProvince[]>>

    @observable developmentDASTA: LookupResponse[] = []
    @task.resolved getLookupDevelopmentDASTA = (async () => {
        if (this.developmentDASTA.length === 0) {
            const { request } = axiosGet<{
                developmentDASTA: LookupResponse[]
            }>({
                url: apiUrl.getLookupDevelopmentDASTA,
            })
            const { data } = await request
            if (data) {
                runInAction(() => {
                    this.developmentDASTA = data.developmentDASTA
                })
            }
            return data?.developmentDASTA || []
        }
    }) as Task<[], Promise<LookupResponse[]>>

    @observable colorChart: LookupResponseColorChart[] = []
    @task.resolved getLookupColorChart = (async () => {
        if (this.colorChart.length === 0) {
            const { request } = axiosGet<{
                colorChart: LookupResponseColorChart[]
            }>({
                url: apiUrl.getLookupColorChart,
            })
            const { data } = await request
            if (data) {
                runInAction(() => {
                    this.colorChart = data.colorChart
                })
            }
            return data?.colorChart || []
        }
    }) as Task<[], Promise<LookupResponseColorChart[]>>

    @observable cluster: LookupResponse[] = []
    @task.resolved getLookupCluster = (async () => {
        if (this.cluster.length === 0) {
            const { request } = axiosGet<ClusterResponse[]>({
                url: apiUrl.getLookupCluster,
            })
            const { data } = await request
            if (data) {
                runInAction(() => {
                    const lookup = data.map((item) => ({
                        label: item.clusterTH,
                        value: item.clusterID,
                        childValue: item.provID,
                        clusterID: item.clusterID,
                    }))
                    this.cluster = lookup
                })
            }
            return data || []
        }
    }) as Task<[], Promise<ClusterResponse[]>>

    // สถานะคำถาม
    @observable questionnaireStatuses: LookupQuestionnaireStatusResponse[] = []
    @task.resolved getLookupQuestionnaireStatus = (async () => {
        if (this.questionnaireStatuses.length === 0) {
            const { request } = axiosGet<LookupQuestionnaireStatusResponse[]>({
                url: apiUrl.getLookupQuestionnaireStatus,
                authorizationType: 'authorization',
            })
            const { data } = await request
            if (data) {
                runInAction(() => {
                    this.questionnaireStatuses = data
                })
            }
            return data || []
        }
    }) as Task<[], Promise<LookupQuestionnaireStatusResponse[]>>

    // คิดเกรด
    @observable questionnaireGrade: LookupQuestionnaireGrade[] = []
    @task.resolved getLookupQuestionnaireGrade = (async () => {
        if (this.questionnaireGrade.length === 0) {
            const { request } = axiosGet<LookupQuestionnaireGrade[]>({
                url: apiUrl.getLookupQuestionnaireGrade,
                authorizationType: 'authorization',
            })
            const { data } = await request
            if (data) {
                runInAction(() => {
                    this.questionnaireGrade = data
                })
            }
            return data || []
        }
    }) as Task<[], Promise<LookupQuestionnaireGrade[]>>

    // ชุมชน
    @observable community: LookupResponseCommunitySearch[] = []
    @task.resolved getLookupCommunitySearch = (async () => {
        if (this.community.length === 0) {
            const { request } = axiosPost<LookupResponseCommunitySearch[]>({
                url: apiUrl.getSearchCommunity,
                params: {},
                authorizationType: 'authorization',
            })

            const { data } = await request
            if (data) {
                runInAction(() => {
                    this.community = data
                })
            }

            return data || []
        }
    }) as Task<[], Promise<LookupResponseCommunitySearch[]>>

    // ชุมชน
    @observable communityCategory: AutoCompleteType[] = []
    @task.resolved getLookupCommunityCategory = (async () => {
        if (this.communityCategory.length === 0) {
            const { request } = axiosGet<LookupResponseCommunityCategory[]>({ url: apiUrl.getLookupCommunityCategory })

            const { data } = await request

            if (data) {
                const lookup = data.map((item) => ({
                    value: item.id,
                    label: item.name,
                }))
                runInAction(() => {
                    this.communityCategory = lookup
                })
            }
            return data || []
        }
    }) as Task<[], Promise<LookupResponseCommunityCategory[]>>

    // รางวัล
    @observable rewards: LookupRewardResponse[] = []
    @task.resolved getLookupReward = (async () => {
        const { request } = axiosGet<LookupResponseReward[]>({ url: apiUrl.getLookupReward })

        const { data } = await request

        if (data) {
            const lookup = data.map((item) => ({
                value: item.id,
                label: item.name,
                institution: item.institution,
            }))
            runInAction(() => {
                this.rewards = lookup
            })
        }
        return data || []
    }) as Task<[], Promise<LookupResponseReward[]>>

    // มาตรฐาน
    @observable standard: LookupRewardResponse[] = []
    @task.resolved getLookupStandard = (async () => {
        const { request } = axiosGet<LookupResponseReward[]>({ url: apiUrl.getLookupStandard })

        const { data } = await request

        if (data) {
            const lookup = data.map((item) => ({
                value: item.id,
                label: item.name,
                institution: item.institution,
            }))
            runInAction(() => {
                this.standard = lookup
            })
        }
        return data || []
    }) as Task<[], Promise<LookupResponseReward[]>>
}
